import React from 'react';
import { Routes, Route, Navigate, NavLink, Link, useParams } from 'react-router-dom';
import { useAuth } from './auth.jsx';
import Login from './pages/Login.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import FilesPage from './pages/FilesPage.jsx';
import PostsPage from './pages/PostsPage.jsx';
import PostDetail from './pages/PostDetail.jsx';
import PostEditor from './pages/PostEditor.jsx';
import CowriteList from './pages/CowriteList.jsx';
import CowriteEditor from './pages/CowriteEditor.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';

function Header() {
  const { user, logout } = useAuth();
  return (
    <header className="bg-white border-b border-medical-100 sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.jpg" alt="K-BIORIA" className="h-9" />
          <div className="leading-tight">
            <div className="text-lg font-bold text-medical-800">리소스공유 포털</div>
            <div className="text-[11px] text-medical-500">Resource Sharing Portal</div>
          </div>
        </Link>
        {user && (
          <nav className="hidden md:flex items-center gap-1 text-sm">
            {[
              ['/files', '파일 공유'],
              ['/notices', '공지사항'],
              ['/discussions', '토론게시판'],
              ['/cowrite', '공동작성'],
            ].map(([to, label]) => (
              <NavLink key={to} to={to}
                className={({ isActive }) =>
                  'px-3 py-2 rounded-md ' + (isActive ? 'bg-medical-50 text-medical-700 font-semibold' : 'text-medical-700 hover:bg-medical-50')
                }>
                {label}
              </NavLink>
            ))}
          </nav>
        )}
        {user && (
          <div className="flex items-center gap-3">
            {user.logo_path && (
              <img src={'/institutions/' + user.logo_path} alt={user.institution_name}
                   title={user.institution_name}
                   className="h-7 w-7 object-contain rounded bg-medical-50 p-0.5 border border-medical-100" />
            )}
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-xs text-medical-500">{user.institution_name}</div>
              <div className="text-sm font-semibold text-medical-800">{user.display_name || user.username}</div>
            </div>
            <button onClick={logout}
              className="text-xs px-2.5 py-1.5 rounded-md border border-medical-200 text-medical-700 hover:bg-medical-50">
              로그아웃
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

// Force CowriteEditor (and its TipTap instance) to fully remount when the doc id changes.
function CowriteEditorMount() {
  const { id } = useParams();
  return <CowriteEditor key={id} />;
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-8 text-center text-medical-500">로딩 중...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="bg-white rounded-2xl shadow-card p-8">
        <h1 className="text-3xl font-bold text-medical-800 mb-2">환영합니다, {user.display_name || user.username}님</h1>
        <p className="text-medical-600">
          <span className="inline-block bg-medical-50 text-medical-700 text-xs font-semibold rounded px-2 py-0.5 mr-2">{user.institution_name}</span>
          소속으로 로그인되었습니다.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {[
            ['/files', '파일 공유', '드래그·드롭 업로드, 버전관리, 태깅'],
            ['/notices', '공지사항', '6개 기관 공동 공지'],
            ['/discussions', '토론게시판', '의견 교환 및 댓글'],
            ['/cowrite', '공동작성', 'Google Docs 스타일 버전·롤백'],
          ].map(([to, t, d]) => (
            <NavLink key={to} to={to}
              className="block bg-medical-50 hover:bg-medical-100 transition rounded-xl p-5 border border-medical-100">
              <div className="text-medical-800 font-semibold">{t}</div>
              <div className="text-xs text-medical-600 mt-1">{d}</div>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <div className="min-h-full flex flex-col">
      <Header />
      <main className="flex-1">
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/" element={<Protected><Home /></Protected>} />
          <Route path="/files" element={<Protected><FilesPage /></Protected>} />
          <Route path="/notices" element={<Protected><PostsPage kind="notices" title="공지사항" /></Protected>} />
          <Route path="/notices/new" element={<Protected><PostEditor kind="notices" /></Protected>} />
          <Route path="/notices/:id" element={<Protected><PostDetail kind="notices" withComments /></Protected>} />
          <Route path="/notices/:id/edit" element={<Protected><PostEditor kind="notices" /></Protected>} />
          <Route path="/discussions" element={<Protected><PostsPage kind="discussions" title="토론게시판" withComments /></Protected>} />
          <Route path="/discussions/new" element={<Protected><PostEditor kind="discussions" /></Protected>} />
          <Route path="/discussions/:id" element={<Protected><PostDetail kind="discussions" withComments /></Protected>} />
          <Route path="/discussions/:id/edit" element={<Protected><PostEditor kind="discussions" /></Protected>} />
          <Route path="/cowrite" element={<Protected><CowriteList /></Protected>} />
          <Route path="/cowrite/:id" element={<Protected><CowriteEditorMount /></Protected>} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </ErrorBoundary>
      </main>
      <footer className="text-center text-xs text-medical-400 py-4">
        © {new Date().getFullYear()} K-BIORIA Resource Sharing Portal
      </footer>
    </div>
  );
}
